import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// TOTP MFA — one device per account.
//
// Three visible states:
//   * NONE      → big CTA to bind an authenticator.
//   * UNVERIFIED → the account started enrolment but never entered a
//                  valid code. Show QR + code deck; buttons: ربط, إعادة
//                  إدخال (fresh QR), إلغاء.
//   * VERIFIED  → مفعّل badge + registration date + إلغاء button.
//
// max_enrolled_factors=1 on the Auth config forbids a second factor,
// so we don't render an "add another" affordance when a verified factor
// is present.
//
// The 6-digit code input is a fanned deck of six cards — the "next"
// card glows Mersal-yellow, each filled card pops and turns warm-ivory,
// wrong code shakes the deck.

type Factor = {
  id: string;
  friendly_name: string | null;
  factor_type: 'totp' | 'phone' | string;
  status: 'verified' | 'unverified';
  created_at: string;
};

type PendingEnroll = {
  factorId: string;
  qrDataUri: string;
  secret: string;
  uri: string;
};

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ar-EG', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Africa/Cairo',
    });
  } catch {
    return iso;
  }
}

// The fanned deck used both for enrolment and for post-login MFA.
function CodeDeck({
  code,
  onChange,
  wobbleAt,
  disabled,
}: {
  code: string;
  onChange: (v: string) => void;
  wobbleAt: number;
  disabled?: boolean;
}) {
  const rotations = [-9, -5.4, -1.8, 1.8, 5.4, 9];
  const offsets = [-6, -2.4, -0.8, 0.8, 2.4, 6];
  const digits = code.padEnd(6, ' ').split('');
  const nextIndex = code.length;
  return (
    <div
      className={`mfa-deck ${wobbleAt ? 'wobble' : ''}`}
      key={wobbleAt}
      onClick={() => {
        const el = document.getElementById('mfa-code-input') as HTMLInputElement | null;
        el?.focus();
      }}
    >
      {digits.map((d, i) => {
        const filled = i < code.length;
        const isNext = !filled && i === nextIndex && !disabled;
        const cls = [
          'mfa-slot',
          filled ? 'mfa-slot--filled' : 'mfa-slot--empty',
          isNext ? 'mfa-slot--next' : '',
        ].filter(Boolean).join(' ');
        return (
          <div
            key={i}
            className={cls}
            style={{
              transform: `translateY(${filled ? '0px' : isNext ? '-4px' : '2px'}) translateX(${offsets[i]}px) rotate(${rotations[i]}deg)`,
              zIndex: filled ? 6 - i : 3,
            }}
          >
            {filled ? d : '•'}
          </div>
        );
      })}
      <input
        id="mfa-code-input"
        className="mfa-hidden-input"
        type="text"
        inputMode="numeric"
        maxLength={7}
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        disabled={disabled}
        aria-label="الكود (6 أرقام)"
      />
    </div>
  );
}

export default function SecurityPage() {
  const [factors, setFactors] = useState<Factor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingEnroll | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [wobbleAt, setWobbleAt] = useState(0);
  const [showSecret, setShowSecret] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    const { data, error: err } = await supabase.auth.mfa.listFactors();
    if (err) setError(err.message);
    else setFactors([...(data.totp ?? []), ...(data.phone ?? [])] as Factor[]);
    setLoading(false);
  };

  const verified = useMemo(() => factors.find((f) => f.status === 'verified'), [factors]);
  const unverified = useMemo(() => factors.find((f) => f.status === 'unverified'), [factors]);

  // If the user closed the page mid-enrolment, resume by re-fetching
  // the QR from that unverified factor's stored URI + secret.
  // Supabase's listFactors doesn't return the QR PNG, so we have to
  // unenroll the stale one and start fresh whenever the visitor wants
  // to continue — same effect for the user, cleaner state.
  const startEnroll = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    // Drop any stale unverified factor first (max_enrolled_factors=1
    // means enroll() would otherwise fail with "already enrolled").
    if (unverified) {
      await supabase.auth.mfa.unenroll({ factorId: unverified.id }).catch(() => {});
    }
    const { data, error: err } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      // Shown in the authenticator app (Google Authenticator / Authy /
      // 1Password) as the account name. Without it the app falls back
      // to the Supabase project URL, which reads as "localhost" or
      // "uakgep…supabase.co" — neither of which is what the operator
      // wants their team to see when they open their vault.
      issuer: 'Mersal App',
      friendlyName: `مرسال ${new Date().toLocaleDateString('ar-EG', { timeZone: 'Africa/Cairo' })}`,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (!data) {
      setError('مقدرناش نبدأ التسجيل — حاول تاني');
      return;
    }
    setPending({
      factorId: data.id,
      qrDataUri: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    });
    setCode('');
    setShowSecret(false);
  };

  // Reissue a brand-new QR + secret without leaving the enrolment card.
  const regenerate = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    await supabase.auth.mfa.unenroll({ factorId: pending.factorId }).catch(() => {});
    const { data, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    setBusy(false);
    if (err || !data) {
      setError(err?.message ?? 'مقدرناش نجدد الـ QR — حاول تاني');
      return;
    }
    setPending({
      factorId: data.id,
      qrDataUri: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    });
    setCode('');
  };

  const cancelEnroll = async () => {
    if (!pending) return;
    setBusy(true);
    await supabase.auth.mfa.unenroll({ factorId: pending.factorId }).catch(() => {});
    setPending(null);
    setCode('');
    await load();
    setBusy(false);
  };

  const verifyEnroll = async () => {
    if (!pending) return;
    const trimmed = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(trimmed)) {
      setError('اكتب الكود اللي في التطبيق (6 أرقام)');
      return;
    }
    setError(null);
    setBusy(true);
    const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({
      factorId: pending.factorId,
    });
    if (chalErr || !chal) {
      setBusy(false);
      setError(chalErr?.message ?? 'خطأ في التحقق');
      return;
    }
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: pending.factorId,
      challengeId: chal.id,
      code: trimmed,
    });
    setBusy(false);
    if (verErr) {
      setError(verErr.message === 'Invalid TOTP code entered' ? 'الكود غلط، جرب تاني' : verErr.message);
      setCode('');
      setWobbleAt(Date.now());
      return;
    }
    setPending(null);
    setCode('');
    setNotice('اتفعّلت الحماية الإضافية — من المرة الجاية هيسألك على الكود عند الدخول');
    await load();
  };

  // Auto-verify on 6 valid digits — matches every big-tech authenticator UX.
  useEffect(() => {
    if (pending && code.length === 6 && !busy) {
      void verifyEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, pending]);

  const removeFactor = async (factorId: string) => {
    const ok = await confirm({
      title: 'إلغاء الحماية الإضافية؟',
      body: 'من غيرها الحساب هيعتمد على كلمة السر فقط. تقدر تربطها تاني بعدين.',
      confirmText: 'إلغاء الحماية',
      tone: 'danger',
    });
    if (!ok) return;
    setError(null);
    const { error: err } = await supabase.auth.mfa.unenroll({ factorId });
    if (err) {
      setError(err.message);
      return;
    }
    setNotice('اتلغت الحماية الإضافية');
    setPending(null);
    setCode('');
    await load();
  };

  return (
    <>
      <style>{`
        /* ==== deck of cards ==== */
        .mfa-deck {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 4px;
          padding: 28px 8px 18px;
          direction: ltr;
          cursor: text;
        }
        .mfa-deck.wobble { animation: mfa-deck-wobble 400ms ease; }
        @keyframes mfa-deck-wobble {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-8px); }
          40%     { transform: translateX(7px); }
          60%     { transform: translateX(-5px); }
          80%     { transform: translateX(3px); }
        }
        .mfa-slot {
          width: 52px; height: 74px;
          border-radius: 12px;
          background: linear-gradient(180deg, #ffffff 0%, #fbfaf5 100%);
          border: 1px solid rgba(0, 144, 145, 0.22);
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
          display: flex; align-items: center; justify-content: center;
          font-family: 'Titillium Web', 'Cairo', system-ui;
          font-weight: 800; font-size: 30px;
          color: #005959;
          transform-origin: 50% 100%;
          transition:
            transform 260ms cubic-bezier(.2,.7,.2,1),
            box-shadow 260ms ease,
            background 260ms ease,
            color 260ms ease;
        }
        .mfa-slot--filled {
          background: linear-gradient(180deg, #ffffff 0%, #fff8dc 100%);
          animation: mfa-slot-pop 260ms ease-out;
        }
        .mfa-slot--next {
          animation: mfa-slot-glow 1.4s ease-in-out infinite;
        }
        .mfa-slot--empty { color: rgba(0, 89, 89, 0.28); }
        @keyframes mfa-slot-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.14); }
          100% { transform: scale(1); }
        }
        @keyframes mfa-slot-glow {
          0%, 100% { box-shadow: 0 6px 22px rgba(254, 200, 48, 0.25), inset 0 0 0 1px rgba(254, 200, 48, 0.55); }
          50%      { box-shadow: 0 8px 30px rgba(254, 200, 48, 0.40), inset 0 0 0 1px rgba(254, 200, 48, 0.85); }
        }
        .mfa-hidden-input {
          position: absolute; inset: 0;
          opacity: 0; cursor: text;
        }
        @media (prefers-reduced-motion: reduce) {
          .mfa-slot, .mfa-slot--filled, .mfa-slot--next, .mfa-deck { animation: none !important; transition: none !important; }
        }

        /* ==== QR panel ==== */
        .qr-panel {
          display: grid;
          grid-template-columns: minmax(240px, auto) 1fr;
          gap: 24px;
          padding: 22px;
          border-radius: 16px;
          background:
            radial-gradient(circle at 100% 0%, rgba(254, 200, 48, 0.10), transparent 60%),
            radial-gradient(circle at 0% 100%, rgba(0, 144, 145, 0.08), transparent 60%),
            #fdfcf8;
          border: 1px solid var(--border, #e1e6ea);
        }
        .qr-frame {
          background: #ffffff;
          border-radius: 14px;
          padding: 14px;
          box-shadow: 0 12px 32px -18px rgba(0, 89, 89, 0.20);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .qr-frame img { display: block; width: 220px; height: 220px; }
        .qr-side { display: flex; flex-direction: column; gap: 14px; }
        .qr-title { font-weight: 800; color: var(--teal-dark, #005959); font-size: 15px; margin: 0; }
        .qr-desc { color: var(--text-secondary, #6d7d87); font-size: 13px; margin: 0; line-height: 1.7; }
        .qr-secret-toggle {
          background: transparent; border: 0;
          color: var(--teal, #009091);
          font-family: inherit; font-size: 13px;
          text-decoration: underline; cursor: pointer;
          text-align: right; padding: 0;
          align-self: flex-start;
        }
        .qr-secret-box {
          display: block;
          padding: 12px;
          background: #ffffff;
          border: 1px dashed var(--border, #e1e6ea);
          border-radius: 10px;
          font-size: 13px; letter-spacing: 1.5px;
          font-family: 'Titillium Web', monospace;
          word-break: break-all;
          color: var(--teal-dark, #005959);
        }

        /* ==== status card ==== */
        .mfa-status {
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; padding: 18px; border-radius: 14px;
          background: linear-gradient(135deg, rgba(0, 144, 145, 0.05), rgba(254, 200, 48, 0.05));
          border: 1px solid var(--border, #e1e6ea);
          flex-wrap: wrap;
        }
        .mfa-status--none {
          background: linear-gradient(135deg, rgba(217, 74, 74, 0.04), rgba(0, 144, 145, 0.03));
        }
        .mfa-status__meta { display: flex; flex-direction: column; gap: 4px; }
        .mfa-status__title { font-weight: 800; font-size: 15px; color: var(--teal-dark, #005959); }
        .mfa-status__desc  { color: var(--text-secondary, #6d7d87); font-size: 13px; }
        .mfa-status__actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .mfa-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 999px;
          font-size: 12px; font-weight: 700;
        }
        .mfa-badge--on  { background: rgba(31, 157, 85, 0.14); color: #197644; border: 1px solid rgba(31, 157, 85, 0.30); }
        .mfa-badge--off { background: rgba(217, 74, 74, 0.10); color: #a72d2d; border: 1px solid rgba(217, 74, 74, 0.30); }
        .mfa-badge__dot { width: 6px; height: 6px; border-radius: 999px; background: currentColor; }
      `}</style>

      <PageHeader
        eyebrow="الأمان"
        title="التحقق بخطوتين (MFA)"
        subtitle="طبقة حماية تانية على حسابك: كلمة السر + كود متغير من تطبيق مصادقة."
      />

      {error && <div className="error-inline">{error}</div>}
      {notice && (
        <div className="error-inline" style={{ background: 'rgba(31,157,85,0.12)', color: 'var(--teal-dark)', border: '1px solid rgba(31,157,85,0.35)' }}>
          {notice}
        </div>
      )}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : (
        <>
          {/* Status header — one line telling the operator whether MFA
              is on, off, or half-configured. */}
          <div className={`mfa-status ${verified ? '' : 'mfa-status--none'}`} style={{ marginBottom: 16 }}>
            <div className="mfa-status__meta">
              {verified ? (
                <>
                  <span className="mfa-badge mfa-badge--on">
                    <span className="mfa-badge__dot" />
                    مفعّل
                  </span>
                  <div className="mfa-status__title">الحماية الإضافية شغّالة</div>
                  <div className="mfa-status__desc">
                    اترتبطت في: {formatCreatedAt(verified.created_at)}
                  </div>
                </>
              ) : (
                <>
                  <span className="mfa-badge mfa-badge--off">
                    <span className="mfa-badge__dot" />
                    مش مفعّل
                  </span>
                  <div className="mfa-status__title">حسابك بيعتمد على كلمة السر بس</div>
                  <div className="mfa-status__desc">
                    نصيحة: اربط تطبيق مصادقة عشان تحمي الحساب حتى لو كلمة السر اتسربت.
                  </div>
                </>
              )}
            </div>
            <div className="mfa-status__actions">
              {verified ? (
                <button
                  className="btn btn-small btn-secondary"
                  onClick={() => void removeFactor(verified.id)}
                >
                  إلغاء الحماية
                </button>
              ) : !pending ? (
                <button className="btn" onClick={() => void startEnroll()} disabled={busy}>
                  {busy ? 'جاري التجهيز...' : unverified ? 'استكمل الربط' : 'اربط تطبيق مصادقة'}
                </button>
              ) : null}
            </div>
          </div>

          {/* Enrolment panel — QR + code deck. Shown only while pending. */}
          {pending && (
            <div className="card" style={{ padding: 20 }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>امسح الـ QR</h2>
              <p style={{ color: 'var(--text-secondary)', marginTop: 0, fontSize: 13 }}>
                افتح تطبيق المصادقة اللي بتستخدمه، دوس على "إضافة حساب" → "امسح QR"، بعدين اكتب الكود اللي هيظهر.
              </p>

              <div className="qr-panel">
                <div className="qr-frame">
                  <img src={pending.qrDataUri} alt="QR" />
                </div>
                <div className="qr-side">
                  <div>
                    <p className="qr-title">الحساب هيسموه "Mersal Admin"</p>
                    <p className="qr-desc">
                      لو التطبيق مش قادر يسحن الكود، اضغط اللينك تحت واكتب المفتاح يدوياً.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="qr-secret-toggle"
                    onClick={() => setShowSecret((v) => !v)}
                  >
                    {showSecret ? 'إخفاء المفتاح' : 'إظهار المفتاح لإدخال يدوي'}
                  </button>
                  {showSecret && <code className="qr-secret-box">{pending.secret}</code>}
                </div>
              </div>

              <CodeDeck
                code={code}
                onChange={setCode}
                wobbleAt={wobbleAt}
                disabled={busy}
              />
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12, marginTop: -4 }}>
                {busy ? 'جاري التحقق...' : 'اكتب الأرقام الست يمين لشمال'}
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                <button className="btn btn-small btn-secondary" onClick={() => void regenerate()} disabled={busy}>
                  إعادة إدخال (QR جديد)
                </button>
                <button className="btn btn-small btn-secondary" onClick={() => void cancelEnroll()} disabled={busy}>
                  إلغاء
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
